import { groupForFamily } from '@/frontend/features/Fde/ontologyGraphGroups'
import type { ModelElement, Relationship } from '@/frontend/features/Fde/types'

export type GraphPerspective = 'processes' | 'people' | 'organization' | 'systems' | 'value' | 'free'
export type GraphDepth = 1 | 2 | 'all'

interface ProjectionOptions {
  elements: ModelElement[]
  relationships: Relationship[]
  anchorIds: string[]
  depth: GraphDepth
  showGaps: boolean
}

function elementId(element: ModelElement) {
  return String(element.id)
}

function elementIs(element: ModelElement, classKey: string) {
  return element.ontology_class?.lineage.includes(classKey) || (element.ontology_class?.key || element.kind) === classKey
}

function normalizedVerb(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleUpperCase()
}

export function ontologyConnectionCounts(relationships: Relationship[]) {
  const counts = new Map<string, number>()
  relationships.forEach((relationship) => {
    const fromId = String(relationship.from.id)
    const toId = String(relationship.to.id)
    counts.set(fromId, (counts.get(fromId) || 0) + 1)
    counts.set(toId, (counts.get(toId) || 0) + 1)
  })
  return counts
}

function matchesPerspective(element: ModelElement, perspective: GraphPerspective) {
  const key = element.ontology_class?.key || element.kind
  const group = groupForFamily(element.ontology_class?.family.key)
  if (perspective === 'processes') return elementIs(element, 'processo')
  if (perspective === 'people') return elementIs(element, 'pessoa')
  if (perspective === 'organization') return ['organizacao', 'equipe', 'pessoa', 'papel'].some((classKey) => elementIs(element, classKey))
  if (perspective === 'systems') return elementIs(element, 'sistema')
  if (perspective === 'value') return group === 'outcomes' || ['problema', 'risco'].includes(key)
  return false
}

export function defaultOntologyAnchors(elements: ModelElement[], relationships: Relationship[], perspective: GraphPerspective) {
  if (perspective === 'free') return []
  const counts = ontologyConnectionCounts(relationships)
  const connected = elements.filter((element) => (counts.get(elementId(element)) || 0) > 0)
  if (perspective === 'organization') return defaultOrganizationAnchors(connected, relationships, counts)
  const candidates = connected.filter((element) => matchesPerspective(element, perspective))
  const ranked = (candidates.length > 0 ? candidates : connected).sort((left, right) => {
    const countDifference = (counts.get(elementId(right)) || 0) - (counts.get(elementId(left)) || 0)
    return countDifference || left.name.localeCompare(right.name, 'pt-BR')
  })
  return ranked.slice(0, 3).map(elementId)
}

function defaultOrganizationAnchors(elements: ModelElement[], relationships: Relationship[], counts: Map<string, number>) {
  const organizational = elements.filter((element) => matchesPerspective(element, 'organization'))
  const hasOutgoing = (element: ModelElement, verb: string, targetClasses: string[]) => relationships.some((relationship) => {
    if (String(relationship.from.id) !== elementId(element) || normalizedVerb(relationship.verb) !== verb) return false
    const target = elements.find((candidate) => elementId(candidate) === String(relationship.to.id))
    return Boolean(target && targetClasses.some((classKey) => elementIs(target, classKey)))
  })
  const organizations = organizational.filter((element) => elementIs(element, 'organizacao'))
  const rootTeams = organizational.filter((element) => elementIs(element, 'equipe') && !hasOutgoing(element, 'PERTENCE A', ['equipe', 'organizacao']))
  const rootPeople = organizational.filter((element) => elementIs(element, 'pessoa') && !hasOutgoing(element, 'REPORTA A', ['pessoa']))
  const candidates = organizations.length > 0 ? organizations : rootTeams.length > 0 ? rootTeams : rootPeople.length > 0 ? rootPeople : organizational
  return candidates.sort((left, right) => {
    const countDifference = (counts.get(elementId(right)) || 0) - (counts.get(elementId(left)) || 0)
    return countDifference || left.name.localeCompare(right.name, 'pt-BR')
  }).slice(0, 3).map(elementId)
}

function adjacencyFor(elements: ModelElement[], relationships: Relationship[]) {
  const adjacency = new Map(elements.map((element) => [elementId(element), new Set<string>()]))
  relationships.forEach((relationship) => {
    const fromId = String(relationship.from.id)
    const toId = String(relationship.to.id)
    adjacency.get(fromId)?.add(toId)
    adjacency.get(toId)?.add(fromId)
  })
  return adjacency
}

function distancesFrom(anchorId: string, adjacency: Map<string, Set<string>>) {
  const distances = new Map<string, number>([[anchorId, 0]])
  const queue = [anchorId]
  queue.forEach((currentId) => {
    const nextDistance = (distances.get(currentId) || 0) + 1
    adjacency.get(currentId)?.forEach((neighborId) => {
      if (distances.has(neighborId)) return
      distances.set(neighborId, nextDistance)
      queue.push(neighborId)
    })
  })
  return distances
}

export function projectOntologyGraph({ elements, relationships, anchorIds, depth, showGaps }: ProjectionOptions) {
  const counts = ontologyConnectionCounts(relationships)
  const gapIds = new Set(elements.filter((element) => !counts.has(elementId(element))).map(elementId))
  const adjacency = adjacencyFor(elements, relationships)
  const distanceLimit = depth === 'all' ? Number.POSITIVE_INFINITY : depth
  const distances = anchorIds.map((anchorId) => distancesFrom(anchorId, adjacency))
  let visibleIds: Set<string>

  if (showGaps) {
    visibleIds = gapIds
  } else if (depth === 'all') {
    visibleIds = new Set(elements.map(elementId))
  } else if (anchorIds.length > 0) {
    visibleIds = new Set(anchorIds)
    distances.forEach((anchorDistances) => {
      anchorDistances.forEach((distance, id) => {
        if (distance <= distanceLimit) visibleIds.add(id)
      })
    })
  } else {
    const connectedIds = elements.filter((element) => counts.has(elementId(element))).map(elementId)
    visibleIds = new Set(connectedIds.length > 0 ? connectedIds : elements.map(elementId))
  }

  const sharedElementIds = new Set<string>()
  visibleIds.forEach((id) => {
    if (anchorIds.includes(id)) return
    const connectedAnchors = distances.filter((anchorDistances) => (anchorDistances.get(id) ?? Number.POSITIVE_INFINITY) <= distanceLimit)
    if (connectedAnchors.length > 1) sharedElementIds.add(id)
  })

  return {
    elements: elements.filter((element) => visibleIds.has(elementId(element))),
    relationships: relationships.filter((relationship) => visibleIds.has(String(relationship.from.id)) && visibleIds.has(String(relationship.to.id))),
    connectionCounts: counts,
    gapCount: gapIds.size,
    sharedElementIds,
  }
}
